import type { ProtoCard } from './seed'
import { nid } from './seed'

export function cloneTree(cards: ProtoCard[]): ProtoCard[] {
	return cards.map((c) => ({
		...c,
		id: nid('copy'),
		children: cloneTree(c.children),
	}))
}

export function findCard(
	cards: ProtoCard[],
	id: string,
): { card: ProtoCard; parent: ProtoCard[]; index: number } | null {
	for (let i = 0; i < cards.length; i++) {
		const c = cards[i]!
		if (c.id === id) return { card: c, parent: cards, index: i }
		const nested = findCard(c.children, id)
		if (nested) return nested
	}
	return null
}

export function removeCard(cards: ProtoCard[], id: string): ProtoCard | null {
	const hit = findCard(cards, id)
	if (!hit) return null
	const [removed] = hit.parent.splice(hit.index, 1)
	return removed ?? null
}

export function isDescendant(root: ProtoCard, id: string): boolean {
	for (const c of root.children) {
		if (c.id === id || isDescendant(c, id)) return true
	}
	return false
}

export function depthOf(cards: ProtoCard[], id: string, depth = 1): number | null {
	for (const c of cards) {
		if (c.id === id) return depth
		const d = depthOf(c.children, id, depth + 1)
		if (d != null) return d
	}
	return null
}

export function subtreeHeight(card: ProtoCard): number {
	if (!card.children.length) return 1
	return 1 + Math.max(...card.children.map(subtreeHeight))
}

export function flattenWithDepth(
	cards: ProtoCard[],
	depth = 1,
): Array<{ card: ProtoCard; depth: number }> {
	const out: Array<{ card: ProtoCard; depth: number }> = []
	for (const c of cards) {
		out.push({ card: c, depth })
		out.push(...flattenWithDepth(c.children, depth + 1))
	}
	return out
}

export function mapTree(
	cards: ProtoCard[],
	fn: (c: ProtoCard) => ProtoCard,
): ProtoCard[] {
	return cards.map((c) => {
		const next = fn(c)
		return { ...next, children: mapTree(c.children, fn) }
	})
}

export function collectLabels(cards: ProtoCard[]): string[] {
	const set = new Set<string>()
	const walk = (list: ProtoCard[]) => {
		for (const c of list) {
			c.labels.forEach((l) => set.add(l))
			walk(c.children)
		}
	}
	walk(cards)
	return [...set].sort((a, b) => a.localeCompare(b))
}

export function collectAuthors(
	cards: ProtoCard[],
): Array<{ id: string; name: string }> {
	const map = new Map<string, string>()
	const walk = (list: ProtoCard[]) => {
		for (const c of list) {
			map.set(c.authorId, c.authorName)
			walk(c.children)
		}
	}
	walk(cards)
	return [...map.entries()]
		.map(([id, name]) => ({ id, name }))
		.sort((a, b) => a.name.localeCompare(b.name))
}

/** Filter Someday: pills AND + text on face/back; show match + ancestors */
export function filterSomedayTree(
	cards: ProtoCard[],
	opts: {
		text: string
		labelPills: string[]
		authorPills: string[]
	},
): ProtoCard[] {
	const q = opts.text.trim().toLowerCase()
	const hasFilters =
		q.length > 0 || opts.labelPills.length > 0 || opts.authorPills.length > 0
	if (!hasFilters) return cards

	const matchSelf = (c: ProtoCard) => {
		if (opts.authorPills.length && !opts.authorPills.includes(c.authorId)) {
			return false
		}
		if (
			opts.labelPills.length &&
			!opts.labelPills.every((l) => c.labels.includes(l))
		) {
			return false
		}
		if (!q) return true
		return (
			c.faceTitle.toLowerCase().includes(q) ||
			c.faceSummary.toLowerCase().includes(q) ||
			c.back.toLowerCase().includes(q)
		)
	}

	const walk = (list: ProtoCard[]): ProtoCard[] => {
		const out: ProtoCard[] = []
		for (const c of list) {
			const kids = walk(c.children)
			const self = matchSelf(c)
			if (self || kids.length) {
				out.push({
					...c,
					children: self ? c.children : kids,
				})
			}
		}
		return out
	}
	return walk(cards)
}
